import { useMemo } from "react";
import { Check, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface Requirement {
  label: string;
  met: boolean;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const requirements: Requirement[] = useMemo(() => [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { label: "Contains a number", met: /[0-9]/.test(password) },
    { label: "Contains special character (!@#$%^&*)", met: /[^A-Za-z0-9]/.test(password) },
  ], [password]);

  const strength = useMemo(() => {
    const metCount = requirements.filter(r => r.met).length;
    return (metCount / requirements.length) * 100;
  }, [requirements]);

  const strengthLabel = useMemo(() => {
    if (password.length === 0) return "";
    if (strength <= 25) return "Weak";
    if (strength <= 50) return "Fair";
    if (strength <= 75) return "Good";
    return "Strong";
  }, [password.length, strength]);

  const strengthColor = useMemo(() => {
    if (strength <= 25) return "bg-destructive";
    if (strength <= 50) return "bg-amber-500";
    if (strength <= 75) return "bg-primary/70";
    return "bg-primary";
  }, [strength]);

  if (password.length === 0) return null;

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Password strength</span>
        <span className={cn(
          "font-medium",
          strength <= 25 && "text-destructive",
          strength > 25 && strength <= 50 && "text-amber-500",
          strength > 50 && strength <= 75 && "text-primary/70",
          strength > 75 && "text-primary"
        )}>
          {strengthLabel}
        </span>
      </div>
      <Progress 
        value={strength} 
        className="h-1.5"
        style={{
          // Override the indicator color based on strength
          ['--progress-color' as string]: strengthColor,
        }}
      />
      <ul className="space-y-1 mt-2">
        {requirements.map((req, index) => (
          <li
            key={index}
            className={cn(
              "flex items-center gap-2 text-xs",
              req.met ? "text-primary" : "text-muted-foreground"
            )}
          >
            {req.met ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
            {req.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
